"use client";

import { useCallback, useRef } from "react";
import { TextToPointCloudConfig, TextDimensions } from "./types";

/**
 * Canvas文本渲染模块
 * 负责将文本渲染为Canvas ImageData
 */
export function useCanvasRenderer() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  // Canvas重用优化 - 避免每次创建新Canvas
  const getCanvas = useCallback(() => {
    if (!canvasRef.current) {
      canvasRef.current = document.createElement("canvas");
    }
    return canvasRef.current;
  }, []);

  /**
   * 初始化Canvas上下文并配置基础属性
   */
  const setupCanvasContext = useCallback((config: TextToPointCloudConfig) => {
    const canvas = getCanvas();
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      throw new Error("无法获取Canvas 2D上下文");
    }

    // 设置Canvas尺寸
    canvas.width = config.canvasWidth;
    canvas.height = config.canvasHeight;

    // 清空画布为黑色背景
    ctx.fillStyle = "#000000";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // 配置文本渲染样式
    ctx.fillStyle = "#ffffff";
    ctx.font = `${config.fontWeight} ${config.fontSize}px ${config.fontFamily}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    return { canvas, ctx };
  }, [getCanvas]);

  /**
   * 测量文本尺寸并计算在Canvas中的覆盖比例
   */
  const measureTextDimensions = useCallback(
    (
      ctx: CanvasRenderingContext2D,
      text: string,
      fontSize: number,
      canvasWidth: number,
      canvasHeight: number,
    ): TextDimensions => {
      const textMetrics = ctx.measureText(text);
      const textWidth = textMetrics.width;
      const textHeight = fontSize; // 近似高度

      return {
        width: textWidth,
        height: textHeight,
        coverageX: textWidth / canvasWidth,
        coverageY: textHeight / canvasHeight,
      };
    },
    [],
  );

  /**
   * 在Canvas上绘制文本并生成数据
   */
  const drawTextToCanvas = useCallback(
    (
      canvas: HTMLCanvasElement,
      ctx: CanvasRenderingContext2D,
      config: TextToPointCloudConfig,
      onCanvasDataUpdate: (dataUrl: string) => void,
    ) => {
      // 绘制文本到画布中心
      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;
      ctx.fillText(config.text, centerX, centerY);

      // 生成并保存数据URL用于预览
      const dataUrl = canvas.toDataURL();
      onCanvasDataUpdate(dataUrl);

      return ctx.getImageData(0, 0, canvas.width, canvas.height);
    },
    [],
  );

  /**
   * 主要的Canvas渲染函数，整合所有步骤
   */
  const renderTextToCanvas = useCallback(
    (
      config: TextToPointCloudConfig,
      onCanvasDataUpdate: (dataUrl: string) => void,
    ): Promise<ImageData & { textCoverageX: number; textCoverageY: number }> => {
      return new Promise((resolve, reject) => {
        try {
          const { canvas, ctx } = setupCanvasContext(config);

          const textDimensions = measureTextDimensions(
            ctx,
            config.text,
            config.fontSize,
            config.canvasWidth,
            config.canvasHeight,
          );

          const imageData = drawTextToCanvas(canvas, ctx, config, onCanvasDataUpdate);

          // 将文本覆盖率信息附加到imageData
          const extendedImageData = imageData as ImageData & {
            textCoverageX: number;
            textCoverageY: number;
          };
          extendedImageData.textCoverageX = textDimensions.coverageX;
          extendedImageData.textCoverageY = textDimensions.coverageY;

          resolve(extendedImageData);
        } catch (err) {
          console.error("Canvas rendering failed:", err);
          reject(err);
        }
      });
    },
    [setupCanvasContext, measureTextDimensions, drawTextToCanvas],
  );

  const cleanup = useCallback(() => {
    if (canvasRef.current) {
      canvasRef.current.remove();
      canvasRef.current = null;
    }
  }, []);

  return {
    renderTextToCanvas,
    cleanup,
  };
}